import {
  ReadonlyWatchable,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { ResourceType } from '@cotera/api';
import { DG } from '@cotera/sdk/core';
import {
  MentionColorVariant,
  MentionGroup,
  MentionOptionDetail,
} from '../components/ui/tiptap/extensions';
import { useTools } from './use-tools-info';

export type MentionType =
  | 'tool'
  | 'document'
  | 'expr'
  | 'agent'
  | 'column'
  | 'resource';

export type ResourceMentionPayload = {
  resourceType: ResourceType;
  resourceId: string;
};

export type MentionData = {
  t: MentionType;
  name: string;
  data: unknown;
} & {
  label?: string;
};

export type MentionOption = {
  label: string;
  data: MentionData;
  color?: MentionColorVariant;
  referenceId?: string;
  group?: string;
  subgroup?: string;
  /** Provider display name for search (e.g. "Google Sheets"). */
  providerLabel?: string;
  /** Shown after the name in compact rows (e.g. search "Suggested" list). */
  typeLabel?: string;
  /** When true, shows a check on the row in the Tools cascade (ready to use). */
  available?: boolean;
  /** Plain-language preview shown in the Tools cascade detail column. */
  detail?: MentionOptionDetail;
};

/**
 * Extracts the provider from a tool key.
 * Tool keys are in format "provider/group/action" (e.g., "github/repos/createBranch").
 */
const getProviderFromToolKey = (toolKey: string): string => {
  const firstSlash = toolKey.indexOf('/');
  return firstSlash > 0 ? toolKey.substring(0, firstSlash) : toolKey;
};

/**
 * Converts a provider key to a display label (e.g., "github" -> "GitHub").
 */
const formatProviderLabel = (provider: string): string => {
  // Handle common providers with specific casing
  const specialCases: Record<string, string> = {
    github: 'GitHub',
    linkedin: 'LinkedIn',
    newsapi: 'NewsAPI',
    'google-sheets': 'Google Sheets',
    'google-jobs': 'Google Jobs',
    'fcp-euro': 'FCP Euro',
    'maps-data': 'Maps Data',
    'snowflake-cortex': 'Snowflake Cortex',
  };

  if (specialCases[provider.toLowerCase()]) {
    return specialCases[provider.toLowerCase()]!;
  }

  // Default: capitalize first letter of each word
  return provider
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const GROUP_ORDER: MentionGroup[] = [
  { id: 'column', label: 'Columns' },
  { id: 'document', label: 'Documents' },
  { id: 'expr', label: 'Expressions' },
  { id: 'agent', label: 'Agents' },
];

export const useGraphMentions = ({
  useModel,
  types,
  relationName,
}: {
  useModel: () => {
    graph: ReadonlyWatchable<DG.BuildExplorer>;
    currentNameInGraph: Watchable<string | null>;
  };
  types: Omit<MentionType, 'tool'>[];
  /** Required when 'column' is in types - the relation to get columns from */
  relationName?: string;
}): { options: MentionOption[]; groups: MentionGroup[] } => {
  const model = useModel();
  const tools = useTools();
  const graph = useWatchableValue(model.graph);
  const currentNameInGraph = useWatchableValue(model.currentNameInGraph);

  const mentionEntries: MentionOption[] = [];

  // Add symbol-level mentions (tool, document, expr, agent)
  const symbolTypes = types.filter(
    (t): t is Exclude<MentionType, 'column'> => t !== 'column'
  );
  if (symbolTypes.length > 0) {
    const symbolMentions = Object.entries(graph.builtNodes())
      .filter(([symbol, result]) => {
        // Can't mention yourself
        if (symbol === currentNameInGraph) {
          return false;
        }
        return symbolTypes.includes(
          result.item.t as Exclude<MentionType, 'column'>
        );
      })
      .map(([name, result]) => {
        const itemType = result.item.t as MentionType;
        return {
          label: name,
          data: {
            t: itemType,
            name,
            data: result.item,
          },
          // No explicit color: the dropdown derives the dot from the mention type
          // (getColorFromMentionType), which is kept in sync with the inserted
          // pill's color so dots and pills always match.
          referenceId:
            result.item.t === 'tool' ? result.item.def.toolId : undefined,
          group: itemType,
        };
      });
    mentionEntries.push(...symbolMentions);
  }

  // Add column mentions if requested
  if (types.includes('column') && relationName) {
    const relNode = graph.forSymbol(relationName);
    if (relNode && relNode.rawNode.t === 'rel') {
      const attributes =
        relNode.builtNode.isOk() && relNode.builtNode.value.item.t === 'rel'
          ? relNode.builtNode.value.item.rel.attributes
          : {};
      const columnMentions = Object.entries(attributes)
        .filter(([colName]) => {
          // Can't mention yourself
          return colName !== currentNameInGraph;
        })
        .map(([colName, colNode]) => ({
          label: colName,
          data: {
            t: 'column' as const,
            name: colName,
            data: colNode,
          },
          group: 'column',
        }));
      mentionEntries.push(...columnMentions);
    }
  }

  // Track unique providers for tool subgroups
  const toolProviders = new Set<string>();

  mentionEntries.push(
    // Deprecated tools (e.g. google/search) stay resolvable for graphs that
    // already mention them, but should not be offered as new mentions.
    ...tools
      .filter((tool) => tool.hidden !== true)
      .map((tool) => {
        const provider = getProviderFromToolKey(tool.key);
        toolProviders.add(provider);

        return {
          label: tool.name,
          data: {
            t: 'tool' as const,
            name: tool.key,
            data: {
              toolId: tool.key,
              toolName: tool.key,
            },
          },
          group: 'tool',
          subgroup: provider,
          referenceId: tool.key,
        };
      })
  );

  // Build tool subgroups from unique providers, sorted alphabetically
  const toolSubgroups = Array.from(toolProviders)
    .sort((a, b) =>
      formatProviderLabel(a).localeCompare(formatProviderLabel(b))
    )
    .map((provider) => ({
      id: provider,
      label: formatProviderLabel(provider),
    }));

  // Filter groups to only include those that have items
  const activeGroups = GROUP_ORDER.filter((group) =>
    mentionEntries.some((entry) => entry.group === group.id)
  ).concat([
    {
      id: 'tool',
      label: 'Tools',
      subgroups: toolSubgroups.length > 0 ? toolSubgroups : undefined,
    },
  ]);

  return { options: mentionEntries, groups: activeGroups };
};
